// Bonds storage - uses DataStore abstraction
import { createLocalStore, generateId, DataStore } from './dataStore';

// Fixed Rate Bond saved configuration
export interface SavedFixedRateBond {
  id: string;
  name: string;
  description?: string;
  // Bond params
  settlementDays: number;
  faceAmount: number;
  couponRate: number;
  accrualDayCounter: string;
  paymentConvention: string;
  redemption: number;
  issueDate: string;
  effectiveDate: string;
  terminationDate: string;
  frequency: string;
  calendar: string;
  convention: string;
  dateGenerationRule: string;
  // Curve reference
  discountCurveId: string;
  // Yield params
  yieldDayCounter: string;
  yieldCompounding: string;
  yieldFrequency: string;
  // Timestamps
  createdAt: string;
  updatedAt: string;
}

// Floating Rate Bond saved configuration
export interface SavedFloatingRateBond {
  id: string;
  name: string;
  description?: string;
  // Bond params
  settlementDays: number;
  faceAmount: number;
  spread: number;
  accrualDayCounter: string;
  paymentConvention: string;
  fixingDays: number;
  inArrears: boolean;
  redemption: number;
  issueDate: string;
  effectiveDate: string;
  terminationDate: string;
  frequency: string;
  calendar: string;
  convention: string;
  dateGenerationRule: string;
  // Index — new: just a ref id
  indexRefId?: string;
  // Index params (legacy — kept for backward compat with existing saved data)
  indexPeriodNumber: number;
  indexPeriodTimeUnit: string;
  indexSettlementDays: number;
  indexCalendar: string;
  indexBusinessDayConvention: string;
  indexEndOfMonth: boolean;
  indexDayCounter: string;
  // Curve references
  discountCurveId: string;
  forecastCurveId: string;
  useSameCurve: boolean;
  // Timestamps
  createdAt: string;
  updatedAt: string;
}

// Storage keys
const FIXED_BONDS_KEY = 'quantra_fixed_bonds';
const FLOATING_BONDS_KEY = 'quantra_floating_bonds';

// Create stores
export const fixedBondStore: DataStore<SavedFixedRateBond> = createLocalStore<SavedFixedRateBond>(FIXED_BONDS_KEY);
export const floatingBondStore: DataStore<SavedFloatingRateBond> = createLocalStore<SavedFloatingRateBond>(FLOATING_BONDS_KEY);

// Re-export generateId
export { generateId };

// Export/Import helpers for Fixed Rate Bonds
export function exportFixedBonds(bonds: SavedFixedRateBond[]) {
  const blob = new Blob([JSON.stringify(bonds, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `fixed-bonds-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function importFixedBonds(file: File): Promise<SavedFixedRateBond[]> {
  const text = await file.text();
  const data = JSON.parse(text);
  const bonds: SavedFixedRateBond[] = Array.isArray(data) ? data : [];
  if (bonds.length === 0) throw new Error('No valid bonds found in file');
  for (const bond of bonds) {
    if (!bond.id) bond.id = generateId();
    await fixedBondStore.save(bond);
  }
  return bonds;
}

// Export/Import helpers for Floating Rate Bonds
export function exportFloatingBonds(bonds: SavedFloatingRateBond[]) {
  const blob = new Blob([JSON.stringify(bonds, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `floating-bonds-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function importFloatingBonds(file: File): Promise<SavedFloatingRateBond[]> {
  const text = await file.text();
  const data = JSON.parse(text);
  const bonds: SavedFloatingRateBond[] = Array.isArray(data) ? data : [];
  if (bonds.length === 0) throw new Error('No valid bonds found in file');
  for (const bond of bonds) {
    if (!bond.id) bond.id = generateId();
    await floatingBondStore.save(bond);
  }
  return bonds;
}
